import { ActionType } from "@sp-crm/core";
import { Spinner } from "components/ui/spinner";
import { WhiteLabelContentMode } from "generated/graphql";
import React from "react";
import { Redirect, Route, Switch } from "react-router";
import { useProductBranding, useWhiteLabelContentMode } from "store/selectors/branding";
import {
    useFeature,
    useFeaturesQuery,
    useIsAllowedInWholeTenant,
    useRegionId,
} from "store/selectors/hooks";
import { AgencySettingsClients } from "./agency-settings-clients";
import { AgencySettingsCommunities } from "./agency-settings-communities";
import { AgencySettingsFax } from "./agency-settings-fax";
import { AgencySettingsGeneral } from "./agency-settings-general";
import { AgencySettingsHomeCareClients } from "./agency-settings-home-care-clients";
import { AgencySettingsHostedFormDetail } from "./agency-settings-hosted-form-detail";
import { AgencySettingsHostedFormIndex } from "./agency-settings-hosted-form-index";
import { AgencySettingsInvoices } from "./agency-settings-invoices";
import { AgencySettingsLeadSources } from "./agency-settings-lead-sources";
import { AgencySettingsNav } from "./agency-settings-nav";
import { AgencySettingsReferrals } from "./agency-settings-referrals";
import { AgencySettingsSignWise } from "./agency-settings-signwise";
import { AgencySettingsTasks } from "./agency-settings-tasks";
import { Settings } from "./customize";
import { LeadSourcesUpsell } from "./lead-sources-upsell";

export const AgencySettings: React.FC<unknown> = () => {
    const editAgencySettingsAllowed = useIsAllowedInWholeTenant(ActionType.UpdateTenant);
    const isPlacementEnabled = useFeature("placement");
    const isHomeCareEnabled = useFeature("homeCarePrototype");
    const isPlacementIntakeEnabled = useFeature("placementLeadIntake");
    const isLeadIntakeEnabled = isHomeCareEnabled || isPlacementIntakeEnabled;
    const isFullBrandContent = useWhiteLabelContentMode() === WhiteLabelContentMode.Full;
    const regionId = useRegionId();
    const featuresQuery = useFeaturesQuery();
    const productBranding = useProductBranding();
    const gatesLoading =
        productBranding.isLoading || (!!regionId && featuresQuery.isLoading);
    if (!editAgencySettingsAllowed) {
        return null;
    }
    return (
        <div className="lg:flex lg:gap-x-5">
            <div className="lg:w-56 lg:shrink-0">
                <AgencySettingsNav />
            </div>
            <div className="lg:flex-1 lg:min-w-0 pb-4">
                <Switch>
                    <Route
                        path="/settings/agency/general"
                        component={AgencySettingsGeneral}
                    />
                    <Route
                        path="/settings/agency/clients"
                        component={AgencySettingsClients}
                    />
                    <Route
                        path="/settings/agency/communities"
                        component={AgencySettingsCommunities}
                    />
                    <Route
                        path="/settings/agency/referrals"
                        component={AgencySettingsReferrals}
                    />
                    <Route
                        path="/settings/agency/tasks"
                        component={AgencySettingsTasks}
                    />
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/agency/invoices"
                            component={AgencySettingsInvoices}
                        />
                    )}
                    <Route path="/settings/agency/fax" component={AgencySettingsFax} />
                    <Route
                        path="/settings/agency/home-care-clients"
                        component={AgencySettingsHomeCareClients}
                    />
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/agency/customize/cc/:questionId"
                            component={Settings}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/agency/customize/cc"
                            component={Settings}
                        />
                    )}
                    <Route
                        path="/settings/agency/workflow/:workflowId?"
                        render={props => (
                            <Redirect
                                to={props.location.pathname.replace("agency/", "")}
                            />
                        )}
                    />
                    <Route
                        path="/settings/agency/signwise"
                        component={AgencySettingsSignWise}
                    />
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/agency/hosted-form/:hostedFormId"
                            component={AgencySettingsHostedFormDetail}
                        />
                    )}
                    {isPlacementEnabled && (
                        <Route
                            path="/settings/agency/hosted-form"
                            component={AgencySettingsHostedFormIndex}
                        />
                    )}
                    {isFullBrandContent && (
                        <Route
                            path="/settings/agency/lead-sources"
                            render={() => {
                                if (gatesLoading) {
                                    return <Spinner />;
                                }
                                return isLeadIntakeEnabled ? (
                                    <AgencySettingsLeadSources />
                                ) : (
                                    <LeadSourcesUpsell />
                                );
                            }}
                        />
                    )}
                    <Route
                        path="/settings/agency"
                        render={() =>
                            gatesLoading ? (
                                <Spinner />
                            ) : (
                                <Redirect to="/settings/agency/general" />
                            )
                        }
                    />
                </Switch>
            </div>
        </div>
    );
};
